import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../BASEURL";

export default function EditHomeCMS() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [hero, setHero] = useState([
    { _id: null, title: "", subtitle: "", description: "", image: null, bgImage: null, imageUrl: "", bgImageUrl: "" },
    { _id: null, title: "", subtitle: "", description: "", image: null, bgImage: null, imageUrl: "", bgImageUrl: "" },
    { _id: null, title: "", subtitle: "", description: "", image: null, bgImage: null, imageUrl: "", bgImageUrl: "" },
  ]);
  const [slideLoading, setSlideLoading] = useState([false, false, false]);
  const [slideError, setSlideError] = useState([null, null, null]);

  // ================= FETCH HERO DATA =================
  useEffect(() => {
    const fetchHero = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/hero`);
        const result = await res.json();

        const slides = Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.hero || [];

        const mapped = slides.map((s) => ({
          _id: s._id, // 🔴 important for update
          title: s.title || "",
          subtitle: s.subtitle || "",
          description: s.description || "",
          image: null,     
          bgImage: null,   
          imageUrl: s.productImage || s.image || "",
          bgImageUrl: s.backgroundImage || s.background || s.bgImage || "",
        }));

        if (mapped.length) setHero(mapped);
      } catch (err) {
        console.error("Hero fetch error:", err);
        setError("Failed to load hero data");
      }
    };

    fetchHero();
  }, []);

  // keep per-slide loading/error arrays in sync with hero length
  useEffect(() => {
    setSlideLoading((prev) => (hero && hero.length ? hero.map(() => false) : prev));
    setSlideError((prev) => (hero && hero.length ? hero.map(() => null) : prev));
  }, [hero.length]);

  // ================= HANDLE INPUT =================
  const handleHeroChange = (index, field, value) => {
    const updated = [...hero];
    updated[index][field] = value;
    setHero(updated);
  };

  // ================= SAVE HERO (UPDATE) =================
  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // 🔁 update each slide separately
      for (let i = 0; i < hero.length; i++) {
        const slide = hero[i];

        if (!slide._id) continue; // safety

        const formData = new FormData();
        formData.append("title", slide.title);
        formData.append("subtitle", slide.subtitle);
        formData.append("description", slide.description);

        if (slide.image) {
          formData.append("productImage", slide.image);
        }

        if (slide.bgImage) {
          formData.append("backgroundImage", slide.bgImage);
        }

        const res = await fetch(`${BASE_URL}/api/hero/${slide._id}`, {
          method: "PUT", // 🔥 UPDATE
          body: formData,
        });

        if (!res.ok) {
          throw new Error(`Failed to update slide ${i + 1}`);
        }
      }

      alert("Hero section updated successfully ✅");
      navigate("/admin/homecms");
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Failed to update hero");
    } finally {
      setLoading(false);
    }
  };

  // ================= SAVE SINGLE SLIDE =================
  const handleSaveSlide = async (index) => {
    setSlideError((prev) => {
      const copy = Array.from(prev || []);
      copy[index] = null;
      return copy;
    });

    setSlideLoading((prev) => {
      const copy = Array.from(prev || []);
      copy[index] = true;
      return copy;
    });

    try {
      const slide = hero[index];
      if (!slide || !slide._id) throw new Error("Slide ID missing");

      const formData = new FormData();
      formData.append("title", slide.title);
      formData.append("subtitle", slide.subtitle);
      formData.append("description", slide.description);

      if (slide.image) formData.append("productImage", slide.image);
      if (slide.bgImage) formData.append("backgroundImage", slide.bgImage);

      const res = await fetch(`${BASE_URL}/api/hero/${slide._id}`, {
        method: "PUT",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to update slide ${index + 1}`);
      }

      alert(`Slide ${index + 1} updated successfully ✅`);
    } catch (err) {
      console.error("Slide save error:", err);
      setSlideError((prev) => {
        const copy = Array.from(prev || []);
        copy[index] = err.message || "Failed to update slide";
        return copy;
      });
    } finally {
      setSlideLoading((prev) => {
        const copy = Array.from(prev || []);
        copy[index] = false;
        return copy;
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto md:p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primaryDark">
          Edit Hero Section
        </h1>
        <button
          onClick={() => navigate("/admin/homecms")}
          className="bg-primary text-white px-6 py-2 rounded hover:bg-primaryDark"
        >
          Back
        </button>
      </div>

      {/* HERO EDITOR */}
      <section className="bg-white p-6 rounded-xl shadow">
        <h2 className="text-xl font-semibold mb-4 text-primaryDark">
          Hero Slides
        </h2>

        {hero.map((item, index) => (
          <div key={index} className="border rounded-lg p-6 mb-6">

            <h3 className="font-semibold mb-4 text-dark/70">
              Slide {index + 1}
            </h3>

            {/* TEXT */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <input
                className="border p-2 rounded w-full"
                placeholder="Title"
                value={item.title}
                onChange={(e) =>
                  handleHeroChange(index, "title", e.target.value)
                }
              />

              <input
                className="border p-2 rounded w-full"
                placeholder="Subtitle"
                value={item.subtitle}
                onChange={(e) =>
                  handleHeroChange(index, "subtitle", e.target.value)
                }
              />

              <textarea
                className="border p-2 rounded w-full"
                placeholder="Description"
                value={item.description}
                onChange={(e) =>
                  handleHeroChange(index, "description", e.target.value)
                }
              />
            </div>

            {/* PRODUCT IMAGE */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Product Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleHeroChange(index, "image", e.target.files[0])
                }
              />

              {item.image ? (
                <img
                  src={URL.createObjectURL(item.image)}
                  className="h-28 mt-2 rounded object-cover"
                  alt="Product Preview"
                />
              ) : item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  className="h-28 mt-2 rounded object-cover"
                  alt="Product Existing"
                />
              ) : null}
            </div>

            {/* BACKGROUND IMAGE */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Background Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleHeroChange(index, "bgImage", e.target.files[0])
                }
              />

              {item.bgImage ? (
                <img
                  src={URL.createObjectURL(item.bgImage)}
                  className="h-28 mt-2 rounded object-cover"
                  alt="Background Preview"
                />
              ) : item.bgImageUrl ? (
                <img
                  src={item.bgImageUrl}
                  className="h-28 mt-2 rounded object-cover"
                  alt="Background Existing"
                />
              ) : null}
            </div>

            {/* PER-SLIDE SAVE */}
            <div className="mt-4 flex items-start space-x-3">
              <button
                onClick={() => handleSaveSlide(index)}
                disabled={slideLoading[index]}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primaryDark disabled:opacity-50"
              >
                {slideLoading[index] ? "Saving..." : `Save Slide ${index + 1}`}
              </button>

              {slideError[index] && (
                <div className="text-sm text-red-700 bg-red-100 px-3 py-2 rounded">
                  {slideError[index]}
                </div>
              )}
            </div>

          </div>
        ))}
      </section>

      {/* ERROR */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* SAVE */}
      {/* <button
        onClick={handleSave}
        disabled={loading}
        className="bg-primary text-white px-8 py-3 rounded hover:bg-primaryDark disabled:opacity-50"
      >
        {loading ? "Saving..." : "Update Hero Section"}
      </button> */}
    </div>
  );
}
